<template>
    <div class="export-settings">
        <div class="layout">
            <div class="left">
                <div class="header">
                    <div class="title">{{ $t('table.whichColumnsToDownload') }}</div>
                    <div class="hint">{{ $t('table.selectFieldsEditNamesFormats') }}</div>
                </div>

                <div class="toolbar">
                    <a-button type="dashed" icon="plus" @click="addColumn">{{ $t('table.addNewColumn') }}</a-button>
                </div>

                <div class="columns">
                    <div class="column-row" v-for="(col, idx) in columns" :key="col.id">
                        <div class="col-content" :class="{ disabled: !col.include }">
                            <div class="col-main">
                                <a-switch :checked="col.include" @change="setInclude(idx, $event)" size="small" />
                                <span class="index">#{{ idx + 1 }}</span>

                                <div class="field">
                                    <label>{{ $t('table.excelColumnName') }}</label>
                                    <a-input :value="col.label" @input="setLabel(idx, $event.target.value)" :placeholder="$t('table.columnName')" size="small" />
                                </div>

                                <div class="field">
                                    <label>{{ $t('table.field') }}</label>
                                    <CustomPicker
                                        :value="col.fieldId"
                                        :items="fieldItems"
                                        idKey="_id"
                                        labelKey="name"
                                        :multiple="false"
                                        label=""
                                        icon="view_column"
                                        @input="setField(idx, $event)"
                                    />
                                </div>

                                <template v-if="colField(col) && !isExcludedType(colField(col))">
                                    <div class="field" v-if="colField(col).valueType === 'date'">
                                        <label>{{ $t('table.dateFormat') }}</label>
                                        <CustomPicker
                                            :value="col.dateFormat || defaultDateFormat"
                                            :items="dateFormatItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="event"
                                            @input="setDateFormat(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'boolean'">
                                        <label>{{ $t('table.booleanFormat') }}</label>
                                        <CustomPicker
                                            :value="col.booleanFormat || 'true-false'"
                                            :items="booleanFormatItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="toggle_on"
                                            @input="setBooleanFormat(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'asset'">
                                        <label>{{ $t('table.assetField') }}</label>
                                        <CustomPicker
                                            :value="col.assetProp || 'cdnUrl'"
                                            :items="assetPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="image"
                                            @input="setAssetProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'user'">
                                        <label>{{ $t('table.userField') }}</label>
                                        <CustomPicker
                                            :value="col.userProp || 'name'"
                                            :items="userPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="person"
                                            @input="setUserProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'contact'">
                                        <label>{{ $t('table.contactField') }}</label>
                                        <CustomPicker
                                            :value="col.contactProp || 'fullname'"
                                            :items="contactPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="contacts"
                                            @input="setContactProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'ticket'">
                                        <label>{{ $t('table.ticketField') }}</label>
                                        <CustomPicker
                                            :value="col.ticketProp || 'subject'"
                                            :items="ticketPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="confirmation_number"
                                            @input="setTicketProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'task'">
                                        <label>{{ $t('table.taskField') }}</label>
                                        <CustomPicker
                                            :value="col.taskProp || 'title'"
                                            :items="taskPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="check_circle"
                                            @input="setTaskProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'conversation'">
                                        <label>{{ $t('table.conversationField') }}</label>
                                        <CustomPicker
                                            :value="col.conversationProp || '_id'"
                                            :items="conversationPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="chat"
                                            @input="setConversationProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'array' && colField(col).fieldType === 'tag'">
                                        <label>{{ $t('table.tagField') }}</label>
                                        <CustomPicker
                                            :value="col.tagProp || 'name'"
                                            :items="tagPropsItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="tag"
                                            @input="setTagProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'referral'">
                                        <label>{{ $t('table.referralField') }}</label>
                                        <CustomPicker
                                            :value="col.referralProp || 'headline'"
                                            :items="referralPropsItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="ads_click"
                                            @input="setReferralProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'channel'">
                                        <label>{{ $t('table.channelField') }}</label>
                                        <CustomPicker
                                            :value="col.channelProp || 'name'"
                                            :items="channelPropItems"
                                            idKey="id"
                                            labelKey="label"
                                            :multiple="false"
                                            label=""
                                            icon="hub"
                                            @input="setChannelProp(idx, $event)"
                                        />
                                    </div>

                                    <div class="field" v-if="colField(col).valueType === 'objectId' && colField(col).fieldType === 'linked-record'">
                                        <label>{{ $t('table.linkedRecord') }}</label>
                                        <div class="muted">{{ $t('table.onlyIdWillBeAdded') }}</div>
                                    </div>
                                </template>

                                <a-button size="small" icon="delete" @click="removeColumn(idx)" class="delete-btn" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="right">
                <div class="header preview-header" @click="togglePreview">
                    <div class="title">
                        <i class="material-icons expand-icon" :class="{ expanded: showPreview }">expand_more</i>
                        Önizleme (ilk 10 kayıt)
                    </div>
                </div>
                <transition name="preview-slide">
                    <div class="preview" v-show="showPreview">
                        <div class="preview-scroll">
                            <table class="preview-table">
                                <thead>
                                    <tr>
                                        <th v-for="c in activeColumns" :key="c.id">{{ c.label }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, rIdx) in previewRows" :key="'r-' + rIdx">
                                        <td v-for="c in activeColumns" :key="'c-' + c.id">{{ row[c.label] }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <div class="footer">
            <a-button @click="$emit('cancel')">İptal</a-button>
            <a-button type="primary" @click="start">Başlat</a-button>
        </div>
    </div>
</template>

<script>
import CustomPicker from "@/components/pickers/CustomPicker.vue";
import moment from "moment";

export default {
    name: "ExportSettings",
    components: { CustomPicker },
    props: {
        fields: { type: Array, required: true },
        records: { type: Array, default: function () { return []; } },
    },
    data() {
        return {
            columns: [],
            showPreview: true,
            defaultDateFormat: "YYYY-MM-DD HH:mm",
            dateFormatItems: [
                { id: "YYYY-MM-DD", label: "YYYY-MM-DD" },
                { id: "YYYY-MM-DD HH:mm", label: "YYYY-MM-DD HH:mm" },
                { id: "DD.MM.YYYY", label: "DD.MM.YYYY" },
                { id: "DD.MM.YYYY HH:mm", label: "DD.MM.YYYY HH:mm" },
                { id: "MM/DD/YYYY", label: "MM/DD/YYYY" },
                { id: "MM/DD/YYYY HH:mm", label: "MM/DD/YYYY HH:mm" },
                { id: "DD MMM YYYY", label: "DD MMM YYYY" },
                { id: "DD MMM YYYY HH:mm", label: "DD MMM YYYY HH:mm" },
                { id: "YYYY/MM/DD", label: "YYYY/MM/DD" },
                { id: "YYYY/MM/DD HH:mm", label: "YYYY/MM/DD HH:mm" },
                { id: "ddd, DD MMM YYYY HH:mm", label: "ddd, DD MMM YYYY HH:mm" },
            ],
            booleanFormatItems: [
                { id: "true-false", label: "true-false" },
                { id: "evet-hayır", label: "evet-hayır" },
                { id: "var-yok", label: "var-yok" },
                { id: "1-0", label: "1-0" },
            ],
            userPropItems: [
                { id: "name", label: "name" },
                { id: "email", label: "email" },
                { id: "_id", label: "_id" },
            ],
            contactPropItems: [
                { id: "fullname", label: "fullname" },
                { id: "email", label: "email" },
                { id: "phone", label: "phone" },
                { id: "whatsappNumber", label: "whatsappNumber" },
                { id: "channel", label: "channel" },
                { id: "platform", label: "platform" },
                { id: "_id", label: "_id" },
            ],
            ticketPropItems: [
                { id: "_id", label: "_id" },
                { id: "subject", label: "subject" },
            ],
            taskPropItems: [
                { id: "_id", label: "_id" },
                { id: "workflow", label: "workflow" },
                { id: "title", label: "title" },
                { id: "description", label: "description" },
            ],
            conversationPropItems: [
                { id: "_id", label: "_id" },
                { id: "createdDate", label: "createdDate" },
            ],
            assetPropItems: [
                { id: "_id", label: "_id" },
                { id: "cdnUrl", label: "cdnUrl" },
            ],
            tagPropsItems: [
                { id: "_id", label: "ID", },
                { id: "name", label: "İsim" },
                { id: "description", label: "Açıklama" },
                { id: "color", label: "Renk" },
            ],
            referralPropsItems: [
                { id: "headline", label: "Başlık" },
                { id: "sourceType", label: "Kaynak Türü" },
                { id: "sourceId", label: "Kaynak ID" },
                { id: "sourceLink", label: "Kaynak Link" },
                { id: "body", label: "İçerik" },
                { id: "createdAt", label: "Oluşturma Tarihi" },
            ],
            channelPropItems: [
                { id: "name", label: "name" },
                { id: "type", label: "type" },
                { id: "_id", label: "_id" },
            ]
        };
    },
    computed: {
        fieldItems() {
            const self = this;
            return (this.fields || []).filter(function (f) { return !self.isExcludedType(f); });
        },
        activeColumns() {
            return (this.columns || []).filter(function (c) { return c.include && c.fieldId; });
        },
        previewRows() {
            const src = (this.records || []).slice(0, 10);
            if (!src.length) return [];
            const rows = [];
            for (let i = 0; i < src.length; i++) {
                const rec = src[i];
                const row = {};
                for (let j = 0; j < this.activeColumns.length; j++) {
                    const col = this.activeColumns[j];
                    const field = (this.fields || []).find(function (f) { return f._id === col.fieldId; });
                    if (!field) continue;
                    row[col.label] = this.renderCellPreview(field, rec, col);
                }
                rows.push(row);
            }
            return rows;
        },
    },
    methods: {
        isExcludedType(field) {
            return field && (field.fieldType === "automation-button" || field.fieldType === "json" || field.fieldType === "event");
        },
        addColumn() {
            const first = this.fieldItems[0];
            const idx = this.columns.length + 1;
            this.columns.push({ id: "col_" + idx + "_" + Date.now(), include: true, fieldId: first ? first._id : null, label: first ? first.name : ("Kolon " + idx) });
        },
        removeColumn(index) { this.columns.splice(index, 1); },
        setInclude(index, value) { this.columns[index].include = !!value; this.columns = [].concat(this.columns); },
        setLabel(index, value) { this.columns[index].label = value; this.columns = [].concat(this.columns); },
        setField(index, fieldId) {
            const field = (this.fields || []).find(function (f) { return f._id === fieldId; });
            this.columns[index].fieldId = fieldId;
            this.columns[index].label = field ? field.name : this.columns[index].label;
            delete this.columns[index].dateFormat;
            delete this.columns[index].booleanFormat;
            delete this.columns[index].assetProp;
            delete this.columns[index].userProp;
            delete this.columns[index].contactProp;
            delete this.columns[index].ticketProp;
            delete this.columns[index].taskProp;
            delete this.columns[index].conversationProp;
            delete this.columns[index].tagProp;
            delete this.columns[index].referralProp;
            delete this.columns[index].channelProp;
            this.columns = [].concat(this.columns);
        },
        setDateFormat(index, value) { this.columns[index].dateFormat = value; this.columns = [].concat(this.columns); },
        setBooleanFormat(index, value) { this.columns[index].booleanFormat = value; this.columns = [].concat(this.columns); },
        setAssetProp(index, value) { this.columns[index].assetProp = value; this.columns = [].concat(this.columns); },
        setUserProp(index, value) { this.columns[index].userProp = value; this.columns = [].concat(this.columns); },
        setContactProp(index, value) { this.columns[index].contactProp = value; this.columns = [].concat(this.columns); },
        setTicketProp(index, value) { this.columns[index].ticketProp = value; this.columns = [].concat(this.columns); },
        setTaskProp(index, value) { this.columns[index].taskProp = value; this.columns = [].concat(this.columns); },
        setConversationProp(index, value) { this.columns[index].conversationProp = value; this.columns = [].concat(this.columns); },
        setTagProp(index, value) { this.columns[index].tagProp = value; this.columns = [].concat(this.columns); },
        setReferralProp(index, value) { this.columns[index].referralProp = value; this.columns = [].concat(this.columns); },
        setChannelProp(index, value) { this.columns[index].channelProp = value; this.columns = [].concat(this.columns); },
        colField(col) { return (this.fields || []).find(function (f) { return f._id === col.fieldId; }); },
        isVisitorRecord(record) {
            // Visitor record'ları table record'larından ayırmak için
            // Visitor record'larının doğrudan properties'i vardır (data wrapper'ı yok)
            return record && !record.data && (record.fullname !== undefined || record.email !== undefined || record.platform !== undefined);
        },
        getVisitorFieldValue(visitor, fieldId) {
            switch (fieldId) {
                case 'fullname':
                    return visitor.fullname || '';
                case 'email':
                    return visitor.email || '';
                case 'phone':
                    return visitor.phone || visitor.whatsappNumber || '';
                case 'platform':
                    return visitor.platform || '';
                case 'lastSeen':
                    return visitor.connectionTime;
                case 'firstSeen':
                    return visitor.createdAt;
                case 'country':
                    return visitor.country || '';
                case 'city':
                    return visitor.city || '';
                case 'os':
                    return visitor.os || '';
                case 'browser':
                    return visitor.browser || '';
                case 'visitingPage':
                    return visitor.visitingPage || '';
                case 'tags':
                    return visitor.tags || [];
                case 'metaReferrals':
                    return visitor._currentReferral || null;
                case 'vipCustomer':
                    return visitor.vipCustomer;
                case 'owner':
                    return visitor.owner || null;
                case 'channel':
                    return visitor.channel || null;
                default:
                    // Custom property'ler için
                    if (fieldId.startsWith('contactProperty_')) {
                        const propKey = fieldId.split('_')[1];
                        return visitor.contactProperties && visitor.contactProperties[propKey] ? visitor.contactProperties[propKey] : '';
                    }
                    return visitor[fieldId] || '';
            }
        },
        renderCellPreview(field, record, col) {
            try {
                // Visitor verileri için özel handling
                let v;
                if (this.isVisitorRecord(record)) {
                    v = this.getVisitorFieldValue(record, field._id);
                } else {
                    // Table records için eski yöntem
                    v = record && record.data ? record.data[field._id] : undefined;
                }
                if (field.valueType === 'date') {
                    const fmt = col.dateFormat || this.defaultDateFormat;
                    return v ? moment(v).format(fmt) : '';
                }
                if (field.valueType === 'boolean') {
                    const fmt = col.booleanFormat || 'true-false';
                    var t = 'true', f = 'false';
                    if (fmt === 'evet-hayır') { t = 'Evet'; f = 'Hayır'; }
                    else if (fmt === 'var-yok') { t = 'Var'; f = 'Yok'; }
                    else if (fmt === '1-0') { t = '1'; f = '0'; }
                    return v ? t : f;
                }
                if (field.valueType === 'asset') {
                    const prop = col.assetProp || 'cdnUrl';
                    return v && typeof v === 'object' ? (v[prop] || '') : '';
                }
                if (field.fieldType === 'tag' && Array.isArray(v)) {
                    const prop = col.tagProp || 'name';
                    return v.map(function(tag) {
                        return tag && typeof tag === 'object' ? (tag[prop] || '') : tag;
                    }).filter(function(x) { return x != null && x !== ''; }).join(', ');
                }
                if (field.valueType === 'objectId') {
                    const ft = field.fieldType; var prop = null;
                    if (ft === 'user') prop = col.userProp || 'name';
                    else if (ft === 'contact') prop = col.contactProp || 'fullname';
                    else if (ft === 'ticket') prop = col.ticketProp || 'subject';
                    else if (ft === 'task') prop = col.taskProp || 'title';
                    else if (ft === 'conversation') prop = col.conversationProp || '_id';
                    else if (ft === 'tag') prop = col.tagProp || 'name';
                    else if (ft === 'linked-record') prop = '_id';
                    else if (ft === 'referral') prop = col.referralProp || 'headline';
                    else if (ft === 'channel') prop = col.channelProp || 'name';
                    if (!prop) return v;
                    return v && typeof v === 'object' ? (v[prop] || '') : v;
                }
                if (field.valueType === 'array') { if (!v) return ''; const arr = Array.isArray(v) ? v : [v]; return arr.filter(function (x) { return x != null; }).join(', '); }
                if (field.valueType === 'json') { return v ? JSON.stringify(v) : ''; }
                return v == null ? '' : String(v);
            } catch (e) { return ''; }
        },
        togglePreview() {
            this.showPreview = !this.showPreview;
        },
        start() {
            const active = this.activeColumns.map(function (c) { return { id: c.id, label: c.label, fieldId: c.fieldId, include: true, dateFormat: c.dateFormat, booleanFormat: c.booleanFormat, assetProp: c.assetProp, userProp: c.userProp, contactProp: c.contactProp, ticketProp: c.ticketProp, taskProp: c.taskProp, conversationProp: c.conversationProp, tagProp: c.tagProp, referralProp: c.referralProp, channelProp: c.channelProp }; });
            this.$emit('start', { columns: active });

            // Emit sonrası modal'ın kapanması için parent'a sinyal gönder
            this.$nextTick(() => {
                this.$emit('cancel');
            });
        },
    },
    created() {
        const base = (this.fields || []).filter((f) => !this.isExcludedType(f));
        for (let i = 0; i < base.length; i++) {
            const f = base[i];
            this.columns.push({ id: 'col_' + (i + 1) + '_' + Date.now(), include: true, fieldId: f._id, label: f.name });
        }
    },
};
</script>

<style scoped lang="scss">
.export-settings {
    display: flex;
    flex-direction: column;

    .layout {
        display: grid;
        grid-template-columns: 2fr 1.4fr;
        gap: 16px;
        padding: 16px 24px;
        max-height: 72vh;
        overflow: scroll;
    }

    .left {
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 8px;
    }
    .right {
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .header {
        margin-bottom: 12px;
        .title { font-weight: 700; font-size: 18px; }
        .hint { color: #888; font-size: 12px; }

    }

    .preview-header {
        cursor: pointer;
        user-select: none;
        .title {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .expand-icon {
            transition: transform 0.3s ease;
            &.expanded {
                transform: rotate(180deg);
            }
        }
    }
    .toolbar { margin-bottom: 8px; }
    .columns { display: flex; flex-direction: column; gap: 6px; }
    .column-row {
        border: 1px solid #eee;
        border-radius: 6px;
        background: #fff;
        max-height: 75px;
        overflow: hidden;
    }
    .col-content {
        padding: 6px 8px;
        &.disabled { opacity: 0.6; pointer-events: none;
            .ant-switch { pointer-events: auto; }
        }
    }
    .col-main {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: nowrap;

        .index {
            color: #999;
            font-size: 11px;
            min-width: 20px;
        }

        .delete-btn {
            margin-left: auto;
            flex-shrink: 0;
        }
    }
    .field {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 120px;
        flex: 1;

        label {
            font-size: 10px;
            color: #666;
            font-weight: 500;
            line-height: 1;
        }
    }
    .muted { color: #888; font-size: 10px; }

    .preview {
        border: 1px solid #eee;
        border-radius: 6px;
        background: #fff;
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
    .preview-scroll {
        overflow: auto;
        flex: 1;
    }
    .preview-table {
        width: max-content;
        min-width: 100%;
        border-collapse: collapse;
        th, td {
            border: 1px solid #f0f0f0;
            padding: 6px 8px;
            font-size: 12px;
            white-space: nowrap;
            min-width: 120px;
        }
        th {
            background: #fafafa;
            position: sticky;
            top: 0;
            z-index: 1;
        }
    }

    .footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 24px; border-top: 1px solid #eee; background: #fff; }

    // Preview slide animation
    .preview-slide-enter-active, .preview-slide-leave-active {
        transition: all 0.3s ease;
        overflow: hidden;
    }
    .preview-slide-enter, .preview-slide-leave-to {
        max-height: 0;
        opacity: 0;
        transform: translateY(-10px);
    }
    .preview-slide-enter-to, .preview-slide-leave {
        max-height: 400px;
        opacity: 1;
        transform: translateY(0);
    }
}
</style>


